import { http, HttpResponse } from 'msw'

import {
  API_BASE_URL,
  DEFAULT_GET_ALL_LIMIT,
  STATUS_CODES,
} from '../utils/constants'

const users = [
  {
    id: '1',
    email: 'admin@example.com',
    name: 'Admin User',
    roles: ['admin', 'user'],
  },
  {
    id: '2',
    email: 'user@example.com',
    name: 'Regular User',
    roles: ['user'],
  },
]

// Mock reporting events data - 30 items
export const mockReportingEvents = [
  { id: '1', reporting_event_name: 'Quarterly Financial Report' },
  { id: '2', reporting_event_name: 'Annual Board Meeting' },
  { id: '3', reporting_event_name: 'Monthly Sales Review' },
  { id: '4', reporting_event_name: 'Product Launch Event' },
  { id: '5', reporting_event_name: 'Team Performance Review' },
  { id: '6', reporting_event_name: 'Budget Planning Session' },
  { id: '7', reporting_event_name: 'Customer Feedback Analysis' },
  { id: '8', reporting_event_name: 'Strategic Planning Workshop' },
  { id: '9', reporting_event_name: 'Market Research Presentation' },
  { id: '10', reporting_event_name: 'Compliance Audit Report' },
  {
    id: '11',
    reporting_event_name:
      'Very Long Event Name That Demonstrates Text Truncation And Tooltip Functionality',
  },
  { id: '12', reporting_event_name: 'Investor Relations Update' },
  { id: '13', reporting_event_name: 'Risk Assessment Review' },
  { id: '14', reporting_event_name: 'Technology Innovation Summit' },
  { id: '15', reporting_event_name: 'Employee Satisfaction Survey' },
  { id: '16', reporting_event_name: 'Sustainability Report' },
  { id: '17', reporting_event_name: 'Digital Transformation Initiative' },
  { id: '18', reporting_event_name: 'Quality Assurance Metrics' },
  { id: '19', reporting_event_name: 'Supply Chain Optimization' },
  { id: '20', reporting_event_name: 'Brand Strategy Workshop' },
  { id: '21', reporting_event_name: 'Cybersecurity Assessment' },
  { id: '22', reporting_event_name: 'Operational Excellence Review' },
  { id: '23', reporting_event_name: 'Customer Success Metrics' },
  { id: '24', reporting_event_name: 'Product Roadmap Planning' },
  { id: '25', reporting_event_name: 'Competitive Analysis' },
  { id: '26', reporting_event_name: 'Partnership Development' },
  { id: '27', reporting_event_name: 'Regulatory Compliance Update' },
  { id: '28', reporting_event_name: 'Innovation Pipeline Review' },
  { id: '29', reporting_event_name: 'Organizational Restructuring' },
  { id: '30', reporting_event_name: 'Global Expansion Strategy' },
]

let mockEventCounter = mockReportingEvents.length

const mockComments = [
  {
    id: '1',
    content: 'Please review the table formatting in row 5',
    created_at: '2024-01-15T10:30:00Z',
    created_by: '1',
    created_by_user: {
      id: '1',
      email: 'admin@example.com',
      first_name: 'John',
      last_name: 'Doe',
    },
    document_version_id: 'doc-1',
    file_name: 'Efficacy Analysis 1.1.html',
    thread_id: '1',
    thread_status: 'open',
    actionable_type: 'actionable',
    deliverable_set: {
      id: 'ds-1',
      deliverable_set_name: 'Zenith Dynamics',
      review_status: 'not_reviewed',
    },
    teamwork_sync_url: 'https://teamwork.com/task/123',
  },
  {
    id: '2',
    content: 'Data in column 3 needs clarification',
    created_at: '2024-01-16T14:20:00Z',
    created_by: '2',
    created_by_user: {
      id: '2',
      email: 'user@example.com',
      first_name: 'Jane',
      last_name: 'Smith',
    },
    document_version_id: 'doc-2',
    file_name: 'Safety Monitoring Plan 2.0.html',
    thread_id: '2',
    thread_status: 'resolved',
    actionable_type: 'not_actionable',
    deliverable_set: {
      id: 'ds-2',
      deliverable_set_name: 'Halcyon Technologies',
      review_status: 'reviewed',
    },
    teamwork_sync_url: null,
  },
]

// Mock notification preferences data
const mockNotificationPreferences = {
  user_id: '1',
  preference_config: {
    comment_new: { email: true, in_app: true },
    comment_resolved: { email: true, in_app: true },
    comment_mention: { email: true, in_app: true },
    workflow_deadline: { email: true, in_app: true },
    deliverable_assign: { email: false, in_app: true },
  },
}

export const handlers = [
  // Sample API endpoints
  http.get(`${API_BASE_URL}/users`, ({ request }) => {
    const authorization = request.headers.get('Authorization')

    if (!authorization?.startsWith('Bearer ')) {
      return HttpResponse.json(
        { message: 'Unauthorized' },
        { status: STATUS_CODES.UNAUTHORIZED }
      )
    }

    return HttpResponse.json(users)
  }),

  http.get(`${API_BASE_URL}/dashboard/stats`, ({ request }) => {
    const authorization = request.headers.get('Authorization')

    if (!authorization?.startsWith('Bearer ')) {
      return HttpResponse.json(
        { message: 'Unauthorized' },
        { status: STATUS_CODES.UNAUTHORIZED }
      )
    }

    return HttpResponse.json({
      totalUsers: 1234,
      activeSessions: 56,
      revenue: 98765.43,
    })
  }),

  // Reporting Events API endpoints
  http.get(`${API_BASE_URL}/admin/v1/reporting-events`, ({ request }) => {
    const url = new URL(request.url)
    const searchQuery = url.searchParams.get('q')
    const limit = Number.parseInt(
      url.searchParams.get('limit') || String(DEFAULT_GET_ALL_LIMIT),
      10
    )

    let filteredEvents = mockReportingEvents

    // Filter by search query
    if (searchQuery?.trim()) {
      const lowerQuery = searchQuery.toLowerCase()
      filteredEvents = mockReportingEvents.filter((event) =>
        event.reporting_event_name.toLowerCase().includes(lowerQuery)
      )
    }

    // Apply limit
    const limitedEvents = filteredEvents.slice(0, limit)

    return HttpResponse.json({
      success: true,
      data: limitedEvents,
      metadata: {
        total: filteredEvents.length,
        limit,
        offset: 0,
        returned: limitedEvents.length,
      },
    })
  }),

  // Create new reporting event
  http.post(
    `${API_BASE_URL}/admin/v1/reporting-events`,
    async ({ request }) => {
      const body = await request.json()
      const { reporting_event_name } = body as { reporting_event_name: string }

      mockEventCounter++
      const newEvent = {
        id: `mock-${mockEventCounter}`,
        reporting_event_name,
      }

      mockReportingEvents.push(newEvent)

      return HttpResponse.json({
        success: true,
        data: [newEvent],
        metadata: {},
      })
    }
  ),

  // Comments list endpoint
  http.post(`${API_BASE_URL}/admin/v1/comments/list`, async ({ request }) => {
    const authorization = request.headers.get('Authorization')

    if (!authorization?.startsWith('Bearer ')) {
      return HttpResponse.json(
        { message: 'Unauthorized' },
        { status: STATUS_CODES.UNAUTHORIZED }
      )
    }

    return HttpResponse.json({
      data: mockComments,
      metadata: {
        page: 1,
        page_size: 10,
        total: mockComments.length,
        total_pages: 1,
      },
    })
  }),

  // Notification Preferences endpoints
  http.get(
    `${API_BASE_URL}/admin/v1/notifications/preferences`,
    ({ request }) => {
      const authorization = request.headers.get('Authorization')

      if (!authorization?.startsWith('Bearer ')) {
        return HttpResponse.json(
          { message: 'Unauthorized' },
          { status: STATUS_CODES.UNAUTHORIZED }
        )
      }

      return HttpResponse.json({
        success: true,
        data: [mockNotificationPreferences],
        metadata: {
          timestamp: new Date().toISOString(),
        },
        pagination: null,
        links: null,
        warnings: null,
      })
    }
  ),

  http.put(
    `${API_BASE_URL}/admin/v1/notifications/preferences`,
    async ({ request }) => {
      const authorization = request.headers.get('Authorization')

      if (!authorization?.startsWith('Bearer ')) {
        return HttpResponse.json(
          { message: 'Unauthorized' },
          { status: STATUS_CODES.UNAUTHORIZED }
        )
      }

      const body = await request.json()
      const updateData = body as {
        preference_config?: {
          new_comment?: { email: boolean; in_app: boolean }
          resolved_comment?: { email: boolean; in_app: boolean }
          mention_comment?: { email: boolean; in_app: boolean }
          deadlines?: { email: boolean; in_app: boolean }
          assignments?: { email: boolean; in_app: boolean }
        }
      }

      // Merge updates with existing preferences
      if (updateData.preference_config) {
        mockNotificationPreferences.preference_config = {
          ...mockNotificationPreferences.preference_config,
          ...updateData.preference_config,
        }
      }

      return HttpResponse.json({
        success: true,
        data: [mockNotificationPreferences],
        metadata: {
          timestamp: new Date().toISOString(),
        },
        pagination: null,
        links: null,
        warnings: null,
      })
    }
  ),
]
